<template>
  <section class="detail-data-wrapper mb-0">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h6 class="form-title mb-0">STATUS DISBURSEMENT</h6>
      <b-button
        size="sm"
        variant="pgiBtn"
        :disabled="isRefreshingStatus"
        @click="$emit('refresh-status')"
      >
        <b-spinner v-if="isRefreshingStatus" small class="mr-1" />
        Refresh Status Disbursement
      </b-button>
    </div>
    <b-row>
      <b-col cols="12" md="6" class="mb-3">
        <p class="detail-label mb-1">Status Proses</p>
        <span :class="pendingStatusClass(transaction.status)">
          {{ pendingStatusLabel(transaction.status) }}
        </span>
      </b-col>
      <b-col cols="12" md="6" class="mb-3">
        <p class="detail-label mb-1">Status Disbursement</p>
        <span :class="resultStatusClass(transaction.statusDisburse)">
          {{ resultStatusLabel(transaction.statusDisburse) }}
        </span>
      </b-col>
      <b-col cols="12" md="6" class="mb-3">
        <p class="detail-label mb-1">Nominal Gadai</p>
        <strong>Rp. {{ formatAmount(nominalGadai) }}</strong>
      </b-col>
      <b-col cols="12" md="6" class="mb-3">
        <p class="detail-label mb-1">Total Diterima Nasabah</p>
        <strong>Rp. {{ formatAmount(totalDiterimaNasabah) }}</strong>
      </b-col>
      <b-col cols="12" md="6" class="mb-3">
        <p class="detail-label mb-1">Diberikan Tunai ke Nasabah</p>
        <strong>{{ labelDiberikanTunaiNasabah }}</strong>
      </b-col>
      <b-col v-if="transaction.status === PENDING_STATUS.PROCESS_SHORTFALL" cols="12" md="6" class="mb-3">
        <p class="detail-label mb-1">Sisa Kekurangan</p>
        <strong>Rp. {{ formatAmount(nominalKekurangan) }}</strong>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import {
  DISBURSE_STATUS,
  PENDING_STATUS,
  getDiberikanTunai,
  getNominalGadai,
  getNominalKekurangan,
  getTotalDiterimaNasabah,
  isFailedDisbursementWithoutStatement,
  isSuccessDisbursementWithoutStatement,
  parseDocumentUrls,
} from './disbursementPendingUtils'

export default {
  name: 'DisbursementPendingStatusSection',
  props: {
    detailGadai: {
      type: Object,
      default: () => ({}),
    },
    disbursementPending: {
      type: Object,
      default: () => ({}),
    },
    transaction: {
      type: Object,
      required: true,
    },
    isRefreshingStatus: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    PENDING_STATUS() {
      return PENDING_STATUS
    },
    DISBURSE_STATUS() {
      return DISBURSE_STATUS
    },
    nominalGadai() {
      return getNominalGadai(this.detailGadai)
    },
    totalDiterimaNasabah() {
      return getTotalDiterimaNasabah(this.disbursementPending)
    },
    diberikanTunai() {
      return getDiberikanTunai(this.disbursementPending)
    },
    nominalKekurangan() {
      return getNominalKekurangan(this.transaction, this.disbursementPending)
    },
    statementDocuments() {
      return parseDocumentUrls(this.disbursementPending.pathFotoStatement)
    },
    labelDiberikanTunaiNasabah() {
      if (
        isFailedDisbursementWithoutStatement(this.transaction, this.disbursementPending)
        || isSuccessDisbursementWithoutStatement(this.transaction, this.disbursementPending)
      ) {
        return 'Tidak Ada'
      }

      return `Rp. ${this.formatAmount(this.diberikanTunai)}`
    },
    transferDocuments() {
      return parseDocumentUrls(this.disbursementPending.pathFotoTransfer)
    },
  },
  methods: {
    formatAmount(value) {
      return this.$helper.addThousandSeparator(value || 0)
    },
    pendingStatusLabel(status) {
      if (status === this.PENDING_STATUS.COMPLETED) {
        if (this.transaction.statusDisburse === this.DISBURSE_STATUS.DANA_DITANGGUHKAN_GAGAL) {
          return this.transferDocuments.length
            ? 'Selesai Dengan Kekurangan'
            : 'Selesai Tanpa Kekurangan'
        }

        return 'Selesai'
      }

      const labels = {
        [this.PENDING_STATUS.PENDING]: 'Pending',
        [this.PENDING_STATUS.WAITING_BANK_RESPONSE]: 'Menunggu Respons Bank',
        [this.PENDING_STATUS.PROCESS_SHORTFALL]: 'Proses Kekurangan',
        [this.PENDING_STATUS.PROCESS_REFUND]: 'Proses Pengembalian',
        [this.PENDING_STATUS.FAILED]: 'Gagal',
      }
      return labels[status] || '-'
    },
    pendingStatusClass(status) {
      if (status === this.PENDING_STATUS.COMPLETED) return 'status-box status-box-success'
      if (status === this.PENDING_STATUS.PROCESS_REFUND) return 'status-box status-box-secondary'
      if (status === this.PENDING_STATUS.PROCESS_SHORTFALL) return 'status-box status-box-danger'
      if (status === this.PENDING_STATUS.FAILED) return 'status-box status-box-danger'
      if (status === this.PENDING_STATUS.WAITING_BANK_RESPONSE) return 'status-box status-box-primary'
      return 'status-box status-box-warning'
    },
    resultStatusLabel(status) {
      const labels = {
        [this.DISBURSE_STATUS.WAIT_APPROVAL]: 'Menunggu Persetujuan',
        [this.DISBURSE_STATUS.APPROVED_ALL]: 'Persetujuan Lengkap',
        [this.DISBURSE_STATUS.DISBURSED]: 'Berhasil Cair',
        [this.DISBURSE_STATUS.DANA_DITANGGUHKAN]: 'Dana Ditangguhkan',
        [this.DISBURSE_STATUS.DANA_DITANGGUHKAN_GAGAL]: 'Gagal Cair',
        [this.DISBURSE_STATUS.CEK_IT]: 'Mohon Hubungi IT',
        [this.DISBURSE_STATUS.CANCELLED]: 'Dibatalkan',
        [this.DISBURSE_STATUS.REJECTED]: 'Ditolak',
      }
      return labels[status] || '-'
    },
    resultStatusClass(status) {
      if (status === this.DISBURSE_STATUS.WAIT_APPROVAL) return 'status-box status-box-wait'
      if (status === this.DISBURSE_STATUS.APPROVED_ALL) return 'status-box status-box-approved'
      if (status === this.DISBURSE_STATUS.DISBURSED) return 'status-box status-box-disbursed'
      if (status === this.DISBURSE_STATUS.DANA_DITANGGUHKAN) return 'status-box status-box-warning'
      if (status === this.DISBURSE_STATUS.DANA_DITANGGUHKAN_GAGAL) return 'status-box status-box-danger'
      if (status === this.DISBURSE_STATUS.CEK_IT) return 'status-box status-box-danger'
      if (status === this.DISBURSE_STATUS.CANCELLED) return 'status-box status-box-cancelled'
      if (status === this.DISBURSE_STATUS.REJECTED) return 'status-box status-box-rejected'
      return 'status-box status-box-default'
    },
  },
}
</script>

<style scoped>
.form-title {
  color: #20a8d8;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 16px;
}

.detail-label {
  color: #73818f;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

</style>
